import { addDays, type ISODate } from '../../shared/dates';
import { dailyTrend, type TrendPoint } from '../../shared/trend';
import { lbToKg } from '../../shared/units';

/** Fixed clock so the example chart does not drift. Not anyone's log. */
export const SAMPLE_TODAY: ISODate = '2026-11-05';
/** Matches the legend on the product chart Patrick sent (a 21-day window). */
export const SAMPLE_TREND_DAYS = 21;
const HISTORY_DAYS = 200;

export const SAMPLE_RANGES = [
  { id: '30', label: '1M', days: 30 },
  { id: '90', label: '3M', days: 90 },
  { id: '180', label: '6M', days: 180 },
  { id: 'all', label: 'All', days: null },
] as const;

export type SampleRangeId = (typeof SAMPLE_RANGES)[number]['id'];

/**
 * Invented weigh-ins: a slow drift down, with about 1–3 lb of daily noise,
 * some above the line and some below. Nothing here is a person's data.
 */
function baseLb(daysAgo: number): number {
  // Steeper over the last month so the moving average itself drifts down, not just the dots.
  if (daysAgo <= 32) return 225.4 + daysAgo * 0.3;
  return 225.4 + 32 * 0.3 + (daysAgo - 32) * 0.04;
}

/** Stable noise, kept inside about ±2.6 lb. */
function noiseLb(daysAgo: number): number {
  const wave = Math.sin(daysAgo * 1.7) * 1.6 + Math.sin(daysAgo * 0.41) * 1.0;
  return Math.round(Math.max(-2.6, Math.min(2.6, wave)) * 10) / 10;
}

function allWeighIns(): Array<{ date: ISODate; kg: number }> {
  const out: Array<{ date: ISODate; kg: number }> = [];
  for (let ago = HISTORY_DAYS; ago >= 1; ago -= 1) {
    // Denser in the last month so the example has a readable scatter, sparser before that.
    const weigh = ago <= 32 ? ago % 2 === 0 : ago % 3 === 0;
    if (!weigh) continue;
    out.push({ date: addDays(SAMPLE_TODAY, -ago), kg: lbToKg(baseLb(ago) + noiseLb(ago)) });
  }
  return out;
}

const ALL = allWeighIns();

export function sampleWeight(rangeId: SampleRangeId): {
  points: Array<{ date: ISODate; kg: number }>;
  trend: TrendPoint[];
  goalKg: number;
  forecastRate: number | null;
  forecastSe: number | null;
} {
  const range = SAMPLE_RANGES.find((r) => r.id === rangeId) ?? SAMPLE_RANGES[0];
  const from = range.days === null ? null : addDays(SAMPLE_TODAY, -range.days);
  const points = from ? ALL.filter((p) => p.date >= from) : ALL;
  const trend = dailyTrend(points, SAMPLE_TREND_DAYS, SAMPLE_TODAY);
  // A gentle continuation of the month's drift, with a band that opens toward the end.
  // Chosen so the example reads like the product chart (about 225–235 lb).
  return {
    points,
    trend,
    // Below this chart on purpose, so the legend can name a goal without a line through the forecast.
    goalKg: lbToKg(180),
    forecastRate: lbToKg(-1.15),
    forecastSe: lbToKg(0.55),
  };
}
